import React, { useState } from "react";
import { Box, Container, Typography, Stack, Grid, Chip } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import CodeIcon from "@mui/icons-material/Code";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import StorageIcon from "@mui/icons-material/Storage";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const MONO   = "'JetBrains Mono', 'Fira Code', ui-monospace, monospace";
const ACCENT = "#E2684A";
const PURPLE = "#7C3AED";
const BG     = "#030014";
const BG2    = "#0F0728";
const LINE   = "#1E2130";
const INK    = "#E7E9EE";
const INK2   = "#B6BBC6";
const INK3   = "#7D8390";
const R      = "14px";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, delay },
});

const solutions = [
  {
    id: "consulting",
    icon: <LightbulbIcon />,
    title: "Strategic AI Consulting",
    subtitle: "Blueprint for Success",
    description: "Navigating the AI landscape requires more than just tools — it demands a strategy. We assess your data maturity, identify high-ROI use cases, and design a scalable roadmap tailored to your specific business goals.",
    details: ["AI Readiness Assessment & Gap Analysis", "High-ROI Use Case Identification", "Technology Roadmap & Vendor Selection", "Governance, Ethics & Compliance Frameworks"],
    color: ACCENT,
    link: "/solutions/strategic-consulting",
  },
  {
    id: "genai",
    icon: <CodeIcon />,
    title: "Generative AI & LLM Engineering",
    subtitle: "Beyond Chatbots: True Cognitive Intelligence",
    description: "Unlock the power of proprietary data with custom Large Language Models and RAG pipelines. We build context-aware, secure, and hallucination-resistant systems that understand the nuance of your industry.",
    details: ["Custom LLM Fine-tuning & Training", "RAG (Retrieval-Augmented Generation) Pipelines", "Multi-Modal AI (Text, Image, Audio)", "Secure On-Premise & Private Cloud Deployment"],
    color: PURPLE,
    link: "/solutions/genai-engineering",
  },
  {
    id: "agents",
    icon: <PrecisionManufacturingIcon />,
    title: "Intelligent Automation (Agents)",
    subtitle: "The Digital Workforce of Tomorrow",
    description: "Replace linear scripts with autonomous agents capable of reasoning, planning, and executing complex workflows. Our voice and text agents handle everything from customer support to supply chain logistics.",
    details: ["Autonomous Agents (Claude Agent SDK)", "Hyper-realistic Voice AI (Vapi, Retell)", "Complex Workflow Automation (n8n, Make)", "24/7 Intelligent Customer Support Bots"],
    color: "#22D3EE",
    link: "/solutions/intelligent-agents",
  },
  {
    id: "data",
    icon: <StorageIcon />,
    title: "Data Engineering & Cloud Ops",
    subtitle: "The Foundation of Intelligence",
    description: "AI is only as good as the data it feeds on. We architect robust, scalable data lakes and pipelines that ensure your models have access to clean, real-time, and secure information.",
    details: ["Modern Data Stack (Snowflake, BigQuery)", "ETL/ELT Pipeline Automation", "Data Quality & Governance", "MLOps & Model Monitoring Infrastructure"],
    color: "#F472B6",
    link: "/solutions/data-engineering",
  },
];

const SolutionsAccordion = () => {
  const [activeId, setActiveId] = useState(solutions[0].id);
  const active = solutions.find(s => s.id === activeId);

  return (
    <Box id="solutions" sx={{ py: { xs: 10, md: 13 }, borderBottom: `1px solid ${LINE}`, background: BG }}>
      <Container maxWidth="lg">
        <motion.div {...fadeUp(0)}>
          <Typography sx={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.16em", textTransform: "uppercase", color: ACCENT, mb: 1.75 }}>
            Our Expertise
          </Typography>
          <Typography sx={{
            fontFamily: "'IBM Plex Serif', serif",
            fontSize: { xs: "1.75rem", md: "2.5rem" },
            fontWeight: 600, letterSpacing: "-0.015em", lineHeight: 1.15,
            color: INK, maxWidth: 720, mb: 7,
          }}>
            End-to-End Artificial Intelligence Services
          </Typography>
        </motion.div>

        <Grid container spacing={3} sx={{ minHeight: "540px" }}>
          {/* Left: Navigation list */}
          <Grid item xs={12} md={5}>
            <Stack spacing={1.5}>
              {solutions.map((item) => (
                <Box
                  key={item.id}
                  onClick={() => setActiveId(item.id)}
                  sx={{
                    p: "18px 20px",
                    borderRadius: R,
                    cursor: "pointer",
                    transition: "all 0.25s",
                    border: `1px solid`,
                    ...(activeId === item.id
                      ? { background: BG2, borderColor: item.color + "50", color: item.color }
                      : { background: "transparent", borderColor: LINE, color: INK3,
                          "&:hover": { borderColor: item.color + "40", background: BG2 + "80" } }
                    ),
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.75}>
                    <Box sx={{
                      color: activeId === item.id ? item.color : INK3,
                      transition: "color 0.25s", display: "flex",
                    }}>
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography sx={{
                        color: activeId === item.id ? INK : INK2,
                        fontWeight: activeId === item.id ? 600 : 400,
                        fontSize: "15.5px", transition: "all 0.25s",
                      }}>
                        {item.title}
                      </Typography>
                      {activeId === item.id && (
                        <Typography sx={{ fontFamily: MONO, fontSize: "11px", color: item.color, mt: 0.4, letterSpacing: "0.06em" }}>
                          {item.subtitle}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* Right: Content panel */}
          <Grid item xs={12} md={7} sx={{ position: "relative" }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeId}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.35 }}
                style={{ height: "100%" }}
              >
                <Box sx={{
                  height: "100%", p: { xs: 3, md: 4 },
                  borderRadius: R, background: BG2,
                  border: `1px solid ${active?.color}30`,
                  position: "relative", overflow: "hidden",
                }}>
                  {/* Corner glow */}
                  <Box sx={{
                    position: "absolute", top: -80, right: -80,
                    width: 260, height: 260, borderRadius: "50%",
                    background: active?.color,
                    filter: "blur(80px)", opacity: 0.1,
                    pointerEvents: "none",
                  }} />

                  <Typography sx={{ fontWeight: 700, color: INK, fontSize: "1.3rem", mb: 1 }}>
                    {active?.title}
                  </Typography>
                  <Typography sx={{ color: INK2, fontSize: "15px", lineHeight: 1.75, mb: 3 }}>
                    {active?.description}
                  </Typography>

                  <Typography sx={{ fontFamily: MONO, fontSize: "10.5px", color: INK3, letterSpacing: "0.12em", textTransform: "uppercase", mb: 1.75 }}>
                    Key Capabilities
                  </Typography>
                  <Grid container spacing={1.5} sx={{ mb: 3 }}>
                    {active?.details.map((detail, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CheckCircleOutlineIcon sx={{ color: ACCENT, fontSize: "14px", flexShrink: 0 }} />
                          <Typography sx={{ color: INK2, fontSize: "14px" }}>{detail}</Typography>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>

                  <Chip
                    label="Learn More →"
                    clickable
                    component="a"
                    href={active?.link}
                    sx={{
                      fontFamily: MONO, fontSize: "12px",
                      border: `1px solid ${active?.color}50`,
                      background: `${active?.color}12`,
                      color: active?.color,
                      borderRadius: "9999px",
                      "&:hover": { background: `${active?.color}20` },
                    }}
                  />
                </Box>
              </motion.div>
            </AnimatePresence>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default SolutionsAccordion;
